type JsonLogLevel = 'DEBUG' | 'ERROR' | 'INFO' | 'LOG' | 'WARN';
export type ConsoleMethod = 'debug' | 'error' | 'info' | 'log' | 'warn';
type LogRuntime = 'browser' | 'script' | 'server';

type MaskingState = {
  applied: boolean;
  fields: string[];
};

type TruncationState = {
  applied: boolean;
  fields: string[];
};

type SerializationState = {
  masking: MaskingState;
  truncation: TruncationState;
};

export type JsonRecord = Record<string, unknown>;
export type JsonLogRecord = JsonRecord & {
  environment: string;
  event: string | null;
  exception: JsonRecord | null;
  fields: JsonRecord;
  level: JsonLogLevel;
  logger: string;
  log_type: string;
  masking: MaskingState & { enabled: boolean };
  message: string;
  protocol: string | null;
  request_id: string | null;
  runtime: LogRuntime;
  service: string;
  stage: string | null;
  timestamp: string;
  trace_id: string | null;
  truncation: TruncationState;
};

export type JsonConsoleLoggerOptions = {
  runtime?: LogRuntime;
  writeJsonLine?: (line: string, record: JsonLogRecord) => void;
};

const APP_NAME = 'front';
const DEFAULT_LOG_TYPE = 'general';
const MAX_SERIALIZE_DEPTH = 6;
const MAX_ARRAY_LENGTH = 50;
const MAX_STRING_LENGTH = 2000;
const MAX_STACK_LENGTH = 12000;
const CONSOLE_LEVEL_WIDTH = 5;
const CONSOLE_SERVICE_WIDTH = 12;
const SENSITIVE_KEY_PATTERNS = [
  'password',
  'token',
  'secret',
  'authorization',
  'cookie',
  'credential',
  'session',
  'api_key',
  'apikey',
  'jwt',
  'refresh_token',
  'access_token',
  'client_secret',
  'set-cookie',
];
const PROHIBITED_BODY_KEYS = new Set(['body', 'payload', 'raw_body', 'request_body', 'response_body']);
const ALLOWED_PROTOCOLS = new Set(['http', 'sse', 'webrtc', 'websocket']);
const ALLOWED_STAGES = new Set(['CHECK', 'CONFIG', 'END', 'ERROR', 'SKIP', 'START']);
const ALLOWED_LOG_TYPES = new Set(['general', 'realtime']);
const STABLE_EVENT_PATTERN = /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/;
const ANSI_ESCAPE_PATTERN = new RegExp(`${String.fromCharCode(27)}\\[[0-?]*[ -/]*[@-~]`, 'g');
const URL_QUERY_PATTERN = /((?:https?:\/\/|\/)[^\s?'"#]+)\?[^\s'"#]*/gi;
let runtimeLogMaskingEnabled: boolean | null = null;

const LEVEL_BY_METHOD: Record<ConsoleMethod, JsonLogLevel> = {
  debug: 'DEBUG',
  error: 'ERROR',
  info: 'INFO',
  log: 'LOG',
  warn: 'WARN',
};

/**
 * 현재 실행 환경을 로그 런타임 구분값으로 변환합니다.
 */
function resolveRuntime(runtime?: LogRuntime): LogRuntime {
  if (runtime) {
    return runtime;
  }

  if (typeof window !== 'undefined') {
    return 'browser';
  }

  return 'server';
}

/** 현재 실행 환경을 Loki 저카디널리티 값으로 정규화합니다. */
function resolveEnvironment(): string {
  const configured = typeof process === 'undefined' ? undefined : (process.env.ENVIRONMENT ?? process.env.NODE_ENV);
  return configured?.trim() || 'development';
}

/** ANSI 제어문자와 빈 문자열을 제거해 한 줄 로그 메시지를 보장합니다. */
function normalizeLogMessage(message: string, state?: SerializationState): string {
  const normalized = message.replace(ANSI_ESCAPE_PATTERN, '').replaceAll('\r', ' ').replaceAll('\n', '\\n').trim() || '로그 이벤트';
  return state ? sanitizeString(normalized, 'message', state) : normalized;
}

/**
 * ISO-8601 타임스탬프를 KST 기준으로 고정 출력합니다.
 */
export function formatTimestamp(date = new Date()): string {
  const pad = (value: number, size = 2) => String(value).padStart(size, '0');
  // 서버/컨테이너 TZ가 UTC여도 로그 출력 시간은 운영 기준인 KST로 통일합니다.
  const kstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000);

  return [
    `${kstDate.getUTCFullYear()}-${pad(kstDate.getUTCMonth() + 1)}-${pad(kstDate.getUTCDate())}`,
    `T${pad(kstDate.getUTCHours())}:${pad(kstDate.getUTCMinutes())}:${pad(kstDate.getUTCSeconds())}.${pad(kstDate.getUTCMilliseconds(), 3)}`,
    '+09:00',
  ].join('');
}

/**
 * 로그 마스킹 활성화 여부를 런타임 설정 우선으로 판단합니다.
 */
function isMaskingEnabled(): boolean {
  const serverValue = typeof process === 'undefined' ? undefined : process.env.LOG_MASKING_ENABLED;
  const configuredValue = runtimeLogMaskingEnabled === null ? serverValue : String(runtimeLogMaskingEnabled);

  return configuredValue?.toLowerCase() !== 'false';
}

/**
 * 런타임 설정 API에서 받은 브라우저 로그 마스킹 상태를 반영합니다.
 */
export function setRuntimeLogMaskingEnabled(value: boolean): void {
  runtimeLogMaskingEnabled = value;
}

/**
 * 민감정보로 취급해야 하는 키인지 대소문자 무관하게 확인합니다.
 */
function isSensitiveKey(key: string): boolean {
  const normalizedKey = key.toLowerCase();
  return SENSITIVE_KEY_PATTERNS.some(pattern => normalizedKey.includes(pattern));
}

/**
 * JSON 직렬화가 가능한 문자열 길이로 제한합니다.
 */
function truncateString(value: string, path: string, truncation: TruncationState, maxLength = MAX_STRING_LENGTH): string {
  if (value.length <= maxLength) {
    return value;
  }

  truncation.applied = true;
  truncation.fields.push(path);
  return `${value.slice(0, maxLength)}...`;
}

function sanitizeString(value: string, path: string, state: SerializationState, maxLength = MAX_STRING_LENGTH): string {
  const sanitized = isMaskingEnabled() ? value.replace(URL_QUERY_PATTERN, '$1?[REDACTED]') : value;

  if (sanitized !== value) {
    state.masking.applied = true;
    state.masking.fields.push(path);
  }

  return truncateString(sanitized, path, state.truncation, maxLength);
}

/**
 * 일반 객체 형태의 로그 부가 필드인지 확인합니다.
 */
function isPlainRecord(value: unknown): value is JsonRecord {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/**
 * Error 객체를 로그 exception 필드 구조로 변환합니다.
 */
function shouldIncludeStack(runtime: LogRuntime): boolean {
  const nodeEnv = typeof process === 'undefined' ? 'production' : process.env.NODE_ENV;
  const configured =
    runtime === 'browser'
      ? typeof process === 'undefined'
        ? undefined
        : process.env.NEXT_PUBLIC_LOG_DEBUG_STACK
      : typeof process === 'undefined'
        ? undefined
        : process.env.FRONT_SERVER_LOG_STACK_ENABLED;

  if (configured !== undefined) {
    return configured.trim().toLowerCase() === 'true';
  }

  return nodeEnv !== 'production';
}

/** Error 객체를 고정된 예외 구조로 변환합니다. */
function serializeError(error: Error, runtime: LogRuntime, state: SerializationState, path = 'exception'): JsonRecord {
  const cause = 'cause' in error ? error.cause : undefined;

  return {
    type: error.name || 'Error',
    message: sanitizeString(error.message, `${path}.message`, state),
    stack: shouldIncludeStack(runtime) && error.stack ? sanitizeString(error.stack, `${path}.stack`, state, MAX_STACK_LENGTH) : null,
    cause:
      cause instanceof Error
        ? {
            type: cause.name || 'Error',
            message: sanitizeString(cause.message, `${path}.cause.message`, state),
          }
        : cause === undefined
          ? null
          : sanitizeValue(cause, `${path}.cause`, state, new WeakSet<object>(), 1, runtime),
  };
}

/**
 * 값 내부의 민감키를 마스킹하면서 JSON 안전 값으로 변환합니다.
 */
function sanitizeValue(value: unknown, path: string, state: SerializationState, visited: WeakSet<object>, depth = 0, runtime: LogRuntime = resolveRuntime()): unknown {
  if (typeof value === 'string') {
    return sanitizeString(value, path, state);
  }

  if (typeof value === 'number' || typeof value === 'boolean' || value === null || value === undefined) {
    return value ?? null;
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (typeof value === 'symbol' || typeof value === 'function') {
    return String(value);
  }

  if (value instanceof Error) {
    return serializeError(value, runtime, state, path);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (depth >= MAX_SERIALIZE_DEPTH) {
    return '[MaxDepth]';
  }

  if (typeof value === 'object') {
    if (visited.has(value)) {
      return '[Circular]';
    }

    visited.add(value);
  }

  if (Array.isArray(value)) {
    const limitedValues = value.slice(0, MAX_ARRAY_LENGTH).map((item, index) => sanitizeValue(item, `${path}[${index}]`, state, visited, depth + 1, runtime));
    if (value.length > MAX_ARRAY_LENGTH) {
      limitedValues.push(`[Truncated ${value.length - MAX_ARRAY_LENGTH} items]`);
      state.truncation.applied = true;
      state.truncation.fields.push(path);
    }

    return limitedValues;
  }

  const result: JsonRecord = {};

  for (const [key, rawValue] of Object.entries(value as JsonRecord)) {
    const nextPath = path ? `${path}.${key}` : key;
    if (isMaskingEnabled() && (isSensitiveKey(key) || PROHIBITED_BODY_KEYS.has(key.toLowerCase()))) {
      result[key] = '***REDACTED***';
      state.masking.applied = true;
      state.masking.fields.push(nextPath);
      continue;
    }

    result[key] = sanitizeValue(rawValue, nextPath, state, visited, depth + 1, runtime);
  }

  return result;
}

/**
 * console 인자 중 Error 객체를 우선 exception 필드로 분리합니다.
 */
function extractException(args: unknown[], runtime: LogRuntime, state: SerializationState): JsonRecord | null {
  const error = args.find(arg => arg instanceof Error);
  return error instanceof Error ? serializeError(error, runtime, state) : null;
}

/**
 * 문자열 prefix에서 logger/service 후보를 추출합니다.
 */
function parseMessagePrefix(message: string): { logger: string; message: string } {
  const matched = message.match(/^\[([^\]]+)]\s*(.*)$/);
  if (!matched) {
    return { logger: 'console', message };
  }

  return {
    logger: matched[1].trim() || 'console',
    message: matched[2].trim() || message,
  };
}

/** Loki label로 사용할 수 있는 고정된 서비스 값으로 정규화합니다. */
function normalizeService(service: string | null, logger: string): string {
  const candidate = (service ?? logger).trim().toLowerCase();

  if (candidate === 'ws-connect' || candidate === 'ws-disconnect') {
    return candidate;
  }
  if (/\b(ws|websocket)\b/.test(candidate)) {
    return 'ws';
  }
  if (candidate.includes('sse')) {
    return 'sse';
  }
  if (/auth|login|logout|session/.test(candidate)) {
    return 'auth';
  }
  if (/weather/.test(candidate)) {
    return 'weather';
  }
  if (/vworld|wms|osm|map|cctv/.test(candidate)) {
    return 'map';
  }
  if (/widget/.test(candidate)) {
    return 'widget';
  }
  if (/admin|menu|tree|systemconfig/.test(candidate)) {
    return 'admin';
  }
  if (/api|proxy|address|geocode/.test(candidate)) {
    return 'api';
  }
  if (candidate.includes('script')) {
    return 'script';
  }
  if (candidate.includes('next')) {
    return 'next-runtime';
  }

  return 'system';
}

function normalizeProtocol(protocol: string | null): string | null {
  const candidate = protocol?.trim().toLowerCase();
  return candidate && ALLOWED_PROTOCOLS.has(candidate) ? candidate : null;
}

function normalizeEvent(event: string | null): string | null {
  const candidate = event?.trim().toLowerCase();
  return candidate && STABLE_EVENT_PATTERN.test(candidate) ? candidate : null;
}

function normalizeStage(stage: string | null): string | null {
  const candidate = stage?.trim().toUpperCase();
  return candidate && ALLOWED_STAGES.has(candidate) ? candidate : null;
}

function normalizeLogType(logType: string): string {
  const candidate = logType.trim().toLowerCase();
  return ALLOWED_LOG_TYPES.has(candidate) ? candidate : DEFAULT_LOG_TYPE;
}

/**
 * 로그 부가 필드에서 request_id와 표준 메타 필드를 분리합니다.
 */
function extractStandardFields(fields: JsonRecord): {
  event: string | null;
  fields: JsonRecord;
  logType: string;
  protocol: string | null;
  requestId: string | null;
  service: string | null;
  stage: string | null;
  traceId: string | null;
} {
  const requestId = typeof fields.request_id === 'string' ? fields.request_id : typeof fields.requestId === 'string' ? fields.requestId : null;
  const traceId = typeof fields.trace_id === 'string' ? fields.trace_id : typeof fields.traceId === 'string' ? fields.traceId : null;
  const service = typeof fields.service === 'string' ? fields.service : null;
  const stage = typeof fields.stage === 'string' ? fields.stage : null;
  const protocol = typeof fields.protocol === 'string' ? fields.protocol : null;
  const event = typeof fields.log_event === 'string' ? fields.log_event : typeof fields.logEvent === 'string' ? fields.logEvent : null;
  const logType = typeof fields.log_type === 'string' ? fields.log_type : DEFAULT_LOG_TYPE;
  const nextFields = { ...fields };

  delete nextFields.request_id;
  delete nextFields.requestId;
  delete nextFields.trace_id;
  delete nextFields.traceId;
  delete nextFields.service;
  delete nextFields.stage;
  delete nextFields.protocol;
  delete nextFields.log_event;
  delete nextFields.logEvent;
  delete nextFields.log_type;

  return { event, fields: nextFields, logType, protocol, requestId, service, stage, traceId };
}

/**
 * console 인자를 message와 fields 구조로 정규화합니다.
 */
function normalizeConsoleArgs(
  args: unknown[],
  state: SerializationState,
  runtime: LogRuntime
): {
  fields: JsonRecord;
  logger: string;
  message: string;
} {
  if (args.length === 0) {
    return { fields: {}, logger: 'console', message: '로그 이벤트' };
  }

  const [firstArg, ...restArgs] = args;
  const visited = new WeakSet<object>();

  if (typeof firstArg === 'string') {
    const parsedMessage = parseMessagePrefix(normalizeLogMessage(firstArg, state));
    const nonErrorArgs = restArgs.filter(arg => !(arg instanceof Error));
    const fields =
      nonErrorArgs.length === 0
        ? {}
        : nonErrorArgs.length === 1 && isPlainRecord(nonErrorArgs[0])
          ? (sanitizeValue(nonErrorArgs[0], 'fields', state, visited, 0, runtime) as JsonRecord)
          : (sanitizeValue({ arguments: nonErrorArgs }, 'fields', state, visited, 0, runtime) as JsonRecord);

    return {
      fields,
      logger: parsedMessage.logger,
      message: parsedMessage.message,
    };
  }

  if (firstArg instanceof Error) {
    return {
      fields: {},
      logger: 'console',
      message: normalizeLogMessage(firstArg.message, state),
    };
  }

  return {
    fields: sanitizeValue({ value: firstArg, arguments: restArgs.filter(arg => !(arg instanceof Error)) }, 'fields', state, visited, 0, runtime) as JsonRecord,
    logger: 'console',
    message: '로그 이벤트',
  };
}

/**
 * DEBUG 로그의 호출 위치를 stack trace에서 추출합니다.
 */
function resolveDebugCallsite(): { line: number | null; path: string | null } {
  const stack = new Error().stack;
  const stackLine = stack
    ?.split('\n')
    .map(line => line.trim())
    .find(line => !line.includes('json-logger') && /\(?(.+):(\d+):(\d+)\)?$/.test(line));
  const matched = stackLine?.match(/\(?(.+):(\d+):(\d+)\)?$/);

  return {
    line: matched ? Number(matched[2]) : null,
    path: matched?.[1] ?? null,
  };
}

/**
 * console 호출을 표준 JSONL 레코드로 변환합니다.
 */
export function createLogRecord(method: ConsoleMethod, args: unknown[], runtime?: LogRuntime): JsonLogRecord {
  const resolvedRuntime = resolveRuntime(runtime);
  const state: SerializationState = {
    masking: { applied: false, fields: [] },
    truncation: { applied: false, fields: [] },
  };
  const normalized = normalizeConsoleArgs(args, state, resolvedRuntime);
  const standardFields = extractStandardFields(normalized.fields);
  const level = LEVEL_BY_METHOD[method];
  const exception = extractException(args, resolvedRuntime, state);
  const record: JsonLogRecord = {
    timestamp: formatTimestamp(),
    level,
    app: APP_NAME,
    environment: resolveEnvironment(),
    runtime: resolvedRuntime,
    logger: normalized.logger,
    service: normalizeService(standardFields.service, normalized.logger),
    protocol: normalizeProtocol(standardFields.protocol),
    event: normalizeEvent(standardFields.event),
    stage: normalizeStage(standardFields.stage),
    message: normalized.message,
    request_id: standardFields.requestId,
    trace_id: standardFields.traceId,
    log_type: normalizeLogType(standardFields.logType),
    fields: standardFields.fields,
    masking: {
      enabled: isMaskingEnabled(),
      applied: state.masking.applied,
      fields: [...new Set(state.masking.fields)],
    },
    truncation: {
      applied: state.truncation.applied,
      fields: [...new Set(state.truncation.fields)],
    },
    exception,
  };

  if (level === 'DEBUG' && shouldIncludeStack(resolvedRuntime)) {
    const callsite = resolveDebugCallsite();
    record.line = callsite.line;
    record.path = callsite.path;
  }

  return record;
}

/**
 * 텍스트 로그에 사용할 key=value 값을 안전하게 직렬화합니다.
 */
function formatTextValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }

  if (typeof value === 'string') {
    return /\s/.test(value) ? `"${value.replaceAll('"', '\\"')}"` : value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return `"${JSON.stringify(value).replaceAll('"', '\\"')}"`;
}

/**
 * 콘솔 prefix 필드 폭을 고정해 로그 라인 시작 위치를 맞춥니다.
 */
function formatPrefixField(value: string, width: number): string {
  if (value.length > width) {
    return value.slice(0, width);
  }

  return value.padEnd(width, ' ');
}

/**
 * 운영자 확인용 콘솔 텍스트 로그를 생성합니다.
 */
export function formatConsoleLogRecord(record: JsonLogRecord): string {
  const stage = record.stage ?? '-';
  const level = formatPrefixField(record.level, CONSOLE_LEVEL_WIDTH);
  const service = formatPrefixField(record.service, CONSOLE_SERVICE_WIDTH);
  const fieldText = Object.entries(record.fields)
    .filter(([key]) => key !== 'request_id' && key !== 'requestId')
    .map(([key, value]) => `${key}=${formatTextValue(value)}`);
  const exception = record.exception;

  if (exception) {
    const exceptionName = typeof exception.type === 'string' ? exception.type : 'Error';
    const exceptionMessage = typeof exception.message === 'string' ? exception.message : '';
    fieldText.push(`exception=${formatTextValue(`${exceptionName}: ${exceptionMessage}`)}`);
  }

  const suffix = fieldText.length > 0 ? ` ${fieldText.join(' ')}` : '';
  return `[${record.timestamp}][${level}][${service}][${stage}] ${record.message}${suffix}`;
}
